import { useState, useEffect } from 'react';
import { RotterdamForm } from './rotterdam-form.jsx';
import { PESTS, STEDEN, REVIEWS, INDEXABLE_CITIES } from './data.js';
import { PestDrawing, IconPhone, IconPin, IconShield, IconCheck } from './icons.jsx';
import { useSEO, buildBreadcrumb } from './seo.jsx';
import { url } from './url.js';
import { PLAAG_DEEP, getPlaagFaqs } from './plaag-deep.js';
import { AndereDienstenCarousel } from './andere-diensten-carousel.jsx';
import { RatingBar } from './rating-bar.jsx';
import { CITY_CONTENT } from './city-extras.jsx';
import { getGoogleAggregate } from './google-reviews.js';

// Live Google-aggregaat hook — gedeeld met stad.jsx/nav.jsx/trust-luxe.jsx
// zodat het reviewtotaal overal automatisch meegroeit zonder rebuild.
function useGoogleAggregate() {
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return agg;
}

// Deterministisch unieke hero-subkop per stad×plaag combo — 1164 combo's
// die anders allemaal dezelfde generic intro hadden. Variant kiezen op
// hash van slug+plaag voor stabiliteit tussen builds.
function uniqueComboIntro(stad, plaag, plaagLow, deep) {
  if (deep && deep.subkop) {
    // PLAAG_DEEP content is al uniek per plaag — combineer met stad-specifiek einde
    const endings = [
      `Dagelijks actief in ${stad.naam} en omliggende gemeenten in ${stad.provincie}.`,
      `Onze bestrijders zijn vertrouwd met ${plaagLow} in ${stad.naam}, gemiddeld binnen 24 uur op locatie.`,
      `In ${stad.naam} pakken wij ${plaagLow} aan met vakkundige methodes en CTGB-gecertificeerde middelen.`,
      `Voor ${stad.naam} en heel ${stad.provincie}: vaste prijs vooraf, spoeddienst beschikbaar.`,
    ];
    let h = 0; const key = stad.slug + plaag.slug;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return `${deep.subkop} ${endings[h % endings.length]}`;
  }
  const variants = [
    `${plaag.nl} in ${stad.naam}? Onze gecertificeerde specialisten staan klaar, meestal binnen 24 uur ter plaatse, vaste prijs vooraf.`,
    `Last van ${plaagLow} in ${stad.naam}? ABC pakt het vakkundig aan met CTGB-gecertificeerde middelen en een grondige nacontrole.`,
    `Voor ${plaagLow}bestrijding in ${stad.naam} bent u bij ABC aan het juiste adres. Spoeddienst 7 dagen per week in heel ${stad.provincie}.`,
    `${stad.naam} en omgeving: van inspectie tot definitieve aanpak van ${plaagLow}. Geen verrassingen, gewoon vakwerk en eerlijke prijzen.`,
  ];
  let h = 0; const key = stad.slug + plaag.slug;
  for (let i = 0; i < key.length; i++) h = (h * 37 + key.charCodeAt(i)) >>> 0;
  return variants[h % variants.length];
}

// ─────────────────────────────────────────────────────────────────────
// StadPlaagPage — combo page: e.g. /den-haag/wespenbestrijding
// SEO-optimised landing page for {city} × {service} keyword combinations.
// Pulls deep content from PLAAG_DEEP so every stad+plaag combo gets the
// same depth as the standalone plaag page, with city context interleaved.
// ─────────────────────────────────────────────────────────────────────
export function StadPlaagPage({ stad, plaag }) {
  // Deep content keyed by plaag.slug — same source as PlaagPage
  const deep = PLAAG_DEEP[plaag.slug] || null;

  // Hero offerte form state (same shape as plaag/stad pages)
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: plaag.nl });
  const [hfSent, setHfSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));
  const submitHero = (e) => { e.preventDefault(); setHfSent(true); };

  // Live Google rating + totaal
  const agg = useGoogleAggregate();
  const ratingStr = String(agg.rating).replace('.', ',');

  const plaagLow = plaag.nl.toLowerCase();
  const stadLow = stad.naam.toLowerCase();

  // City-aware FAQs — merge deep.faqs with stad-specific FAQs at the top
  const cityFaqs = [
    {
      q: `Hoe snel zijn jullie bij mij in ${stad.naam} voor ${plaagLow}?`,
      a: `Onze specialisten zijn dagelijks actief in ${stad.naam} en omliggende gemeenten in ${stad.provincie}. Gemiddeld zijn wij binnen 24 uur bij u, bij spoed (wespennest, ratten zichtbaar overdag, kakkerlakken in horeca) regelmatig nog dezelfde dag.`,
    },
    {
      q: `Wat kost ${plaagLow}bestrijding in ${stad.naam}?`,
      a: `De prijs voor ${plaagLow}bestrijding in ${stad.naam} hangt af van de omvang en aanpak. De definitieve prijs hoort u altijd telefonisch vóór de afspraak. Bel 085 500 50 50 voor een eerlijke prijsindicatie aan de telefoon, geen verborgen kosten.`,
    },
    {
      q: `Werken jullie ook 's avonds en in het weekend in ${stad.naam}?`,
      a: `Ja, wij zijn 7 dagen per week actief in ${stad.naam} van ma t/m vr 07:00-22:00 en za-zo 09:00-20:00. Spoeddiensten voor ${plaagLow} zijn 24/7 beschikbaar, ook 's nachts.`,
    },
  ];
  // cityFaqs eerst (lokale info), dan plaag-FAQs incl. de garantie-FAQ aan
  // het einde (getPlaagFaqs voegt de gedeelde garantie-uitleg automatisch toe).
  const faqs = [...cityFaqs, ...getPlaagFaqs(plaag.slug)];

  // FAQPage schema for rich snippets in Google
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".faq-question", ".faq-answer"],
    },
  };

  // City+service title — short for Google snippet (~60 chars max).
  // Brand suffix wordt door useSEO conditioneel toegevoegd als er ruimte is.
  const metaTitle = `${plaag.nl}bestrijding ${stad.naam}`;
  // Meta description: hand-crafted natuurlijke zinnen, géén afkapping met '…'.
  // Eerdere generator gebruikte deep.subkop + cta en kapte vaak mid-zin af
  // ("Onze gecertificeerde… Bel 085…"). Nu: vaste template per slot die
  // gegarandeerd onder 155 chars blijft voor élke stad×plaag combinatie.
  // Lengte-check: max stad ("Krimpenerwaard"=14) + max plaag ("Kakkerlakken"=12)
  // → template levert ~140-148 chars op. Veilig binnen 155.
  const metaDesc = `${plaag.nl}bestrijding in ${stad.naam}, vakkundig en met bron-aanpak. Vaste prijs vooraf, gemiddeld binnen 24 uur op locatie. Bel 085 500 50 50.`;

  // Duplicate-content strategie (SEO-audit fase 4.2): alleen de top-15
  // strategische steden mogen Google-indexable zijn. Overige 102 steden
  // zijn intern bereikbaar (footer, "andere diensten in {stad}"-blokken)
  // maar krijgen 'noindex, follow' zodat ze niet ranken voor dezelfde
  // longtail-keywords als de standalone /werkgebied/{stad}/ pagina. Volg
  // INDEXABLE_CITIES in data.js voor de actuele whitelist.
  const isIndexable = INDEXABLE_CITIES.has(stad.slug);

  useSEO({
    title: metaTitle,
    description: metaDesc,
    path: `/${stad.slug}/${plaag.slug}bestrijding`,
    noindex: !isIndexable,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "PestControlService",
        "serviceType": `${plaag.nl}bestrijding`,
        "name": `${plaag.nl}bestrijding ${stad.naam}, ABC Ongediertebestrijding`,
        "description": `${plaag.nl}bestrijding voor particulier en zakelijk in ${stad.naam} en omliggende gemeenten in ${stad.provincie}. ${plaag.intro}`,
        "provider": { "@id": "https://www.abcongedierte.nl/#organization" },
        "areaServed": { "@type": "City", "name": stad.naam },
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "priceCurrency": "EUR",
            "description": "Eerlijke prijs telefonisch vóór de afspraak"
          },
        },
        // 2026-05-29: aggregateRating verwijderd van combo-pages (1755 stuks!).
        // Dezelfde bedrijfsrating 1755x claimen = textbook rich-results spam.
        // Sterren-snippets blijven op homepage + /reviews/ pagina.
      },
      faqSchema,
      buildBreadcrumb([
        { name: "Home", url: "/" },
        { name: "Werkgebied", url: "/werkgebied" },
        { name: stad.naam, url: `/werkgebied/${stad.slug}` },
        { name: `${plaag.nl}bestrijding`, url: `/${stad.slug}/${plaag.slug}bestrijding` },
      ]),
    ],
  });

  return (
    <div className="page-anim">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="breadcrumb">
            <a href={url.home()}>Home</a>
            <span className="sep">/</span>
            <a href={url.stad(stad.slug)}>{stad.naam}</a>
            <span className="sep">/</span>
            <span style={{ color: 'rgba(255,255,255,.9)' }}>{plaag.nl}bestrijding</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 48, alignItems: 'center' }} className="ph-grid">
            <div>
              <div className="hero-badge">
                <IconPin width="14" height="14" />&nbsp;{stad.provincie} · {plaag.lat}
              </div>
              <h1 style={{ fontSize: 'clamp(1.7rem,4.5vw,3.2rem)', fontWeight: 900, color: 'white', lineHeight: 1.1, margin: '14px 0 18px', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                {plaag.nl}bestrijding<br className="hide-mobile"/>
                {' '}in <span style={{ color: 'var(--orange)' }}>{stad.naam}</span>
              </h1>
              {deep && (
                <p style={{ color: 'var(--orange)', fontWeight: 700, fontSize: '.95rem', margin: '0 0 12px' }}>
                  {deep.h1Sub}
                </p>
              )}
              <p style={{ color: 'rgba(255,255,255,.78)', fontSize: '1.05rem', maxWidth: '54ch', marginBottom: 20 }}>
                {uniqueComboIntro(stad, plaag, plaagLow, deep)}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                <a href="tel:0855005050" className="btn-bel-lg"><IconPhone width="16" height="16" /> 085 500 50 50</a>
                <a href={url.offerte()} className="btn-form-lg" style={{ borderColor: 'var(--orange)' }}>Gratis offerte</a>
              </div>
              <div style={{
                display: 'inline-flex', gap: 14, padding: '10px 18px',
                background: 'rgba(255,255,255,0.08)', borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.15)', fontSize: '.85rem', color: 'rgba(255,255,255,.85)',
              }}>
                <span><strong style={{ color: 'var(--orange)' }}>★</strong> Eerlijk tarief vooraf</span>
                <span style={{ opacity: .4 }}>·</span>
                <span>≤ 24u reactietijd</span>
                <span style={{ opacity: .4 }}>·</span>
                <span>Gecertificeerde partners</span>
              </div>
              {/* Garantie-stamp voor stad×wespen combo-pagina's: alleen
                  wanneer deep.garantieBadge is gezet (= wespen). Hetzelfde
                  component-pattern als PlaagPage hero. */}
              {deep && deep.garantieBadge && (
                <div className="garantie-stamp" role="note" aria-label="100 procent seizoensgarantie" style={{ marginTop: 22 }}>
                  <div className="garantie-stamp-icon" aria-hidden="true">100%</div>
                  <div className="garantie-stamp-text">
                    <span className="garantie-stamp-title">
                      {deep.garantieBadgeTitle || '100% Seizoensgarantie'}
                    </span>
                    <span className="garantie-stamp-sub">
                      {deep.garantieBadgeSub || 'Keert het nest binnen het seizoen terug? Wij behandelen kosteloos opnieuw.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Reviews onderaan linker kolom, identiek aan home-hero. */}
              <div className="hero-cta-rating">
                <RatingBar />
              </div>
            </div>
            <RotterdamForm variant="hero" cityName={stad.naam} />
          </div>
          <style>{`@media (max-width:880px){ .ph-grid{ grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
        </div>
      </section>

      {/* ─── RECOGNIZE / SIGNALEN ─────────────────────────────── */}
      {deep && (
        <section className="section">
          <div className="container" style={{ maxWidth: 900 }}>
            <div className="section-tag">Herkent u dit in {stad.naam}?</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, color: 'var(--navy)', margin: '12px 0 18px' }}>
              Wanneer is het tijd om een specialist in {stad.naam} te bellen?
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--text)' }}>{deep.recognize}</p>
          </div>
        </section>
      )}

      {deep && (
        <section className="section section-bg">
          <div className="container" style={{ maxWidth: 900 }}>
            <div className="section-tag">Signalen</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, color: 'var(--navy)', margin: '12px 0 18px' }}>
              {deep.signalsHead}
            </h2>
            <ul className="checklist" style={{ marginBottom: 22 }}>
              {deep.signalsList.map(s => <li key={s}><IconCheck /> {s}</li>)}
            </ul>
            <p style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>{deep.entry}</p>
          </div>
        </section>
      )}

      {/* ─── METHOD / AANPAK ─────────────────────────────────── */}
      {deep && (
        <section className="section">
          <div className="container">
            <div className="sh">
              <div className="section-tag">Onze aanpak in {stad.naam}</div>
              <h2>Hoe wij {plaagLow} aanpakken in <em>{stad.naam}</em></h2>
              <p>Een vaste, gecertificeerde werkwijze, voor elke woning of bedrijf in {stad.naam}.</p>
            </div>
            <div className="process-grid">
              {deep.methodSteps.map((s, i) => {
                const imgIdx = Math.min(i, 3);
                const imgSrc = s.img || `/assets/inspecteur-stap-${imgIdx + 1}.png`;
                return (
                  <div key={s.t} className="ps">
                    <div className="ps-inspector">
                      <picture>
                        <source srcSet={imgSrc.replace(/\.png$/, '.webp')} type="image/webp" />
                        <img
                          src={imgSrc}
                          alt={`Inspecteur stap ${i + 1}: ${s.t}`}
                          className="ps-inspector-img"
                          loading="lazy"
                          decoding="async"
                          width="220"
                          height="260"
                        />
                      </picture>
                    </div>
                    <h3>{s.t}</h3>
                    <p>{s.d}</p>
                  </div>
                );
              })}
            </div>
            {deep.methodNote && (
              <p style={{ marginTop: 28, padding: '14px 18px', background: 'var(--orange-light)', borderRadius: 12, color: 'var(--orange-dark)', fontSize: '.92rem', lineHeight: 1.6 }}>
                <strong>Let op:</strong> {deep.methodNote}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ─── WHY PROFESSIONAL + COSTS ─────────────────────────── */}
      {deep && (
        <section className="section section-bg">
          <div className="container">
            <div className="plaag-detail">
              <div className="plaag-content">
                <div className="section-tag">Waarom een specialist?</div>
                <h2>Waarom {plaagLow}bestrijding in {stad.naam} <em>professioneel</em> aangepakt moet worden</h2>
                <p>{deep.whyPro}</p>

                <h3 style={{ marginTop: 26 }}>Kosten {plaagLow}bestrijding in {stad.naam}</h3>
                <p>{deep.costs}</p>

                <h3 style={{ marginTop: 26 }}>Waarom klanten in {stad.naam} voor ABC kiezen</h3>
                <ul>
                  <li>Dagelijks actief in {stad.naam} en omliggende gemeenten in {stad.provincie}, gemiddeld &le; 24 uur op locatie</li>
                  <li>Werkt met gecertificeerde partners (IPM)</li>
                  <li>{ratingStr}/5 sterren op {agg.total} Google reviews</li>
                  <li>Vakkundige nacontrole na elke behandeling</li>
                  <li>Vaste prijs vooraf, geen verrassingen achteraf</li>
                  <li>Ook voor huurders, VvE's, horeca en bedrijven</li>
                </ul>
              </div>

              <aside className="plaag-side">
                <div className="plaag-price-card">
                  <div style={{ fontSize: '.78rem', color: 'var(--orange-dark)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Prijs op aanvraag
                  </div>
                  <div className="plaag-price" style={{ fontSize: '1.55rem', lineHeight: 1.15 }}>Telefonisch vooraf</div>
                  <div className="plaag-price-sub">Eerlijk tarief voor {stad.naam}, geen verrassingen achteraf</div>
                  <ul style={{
                    listStyle: 'none', padding: 0, margin: '16px 0 0',
                    display: 'flex', flexDirection: 'column', gap: 8,
                    textAlign: 'left',
                  }}>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Definitieve prijs telefonisch vóór de afspraak</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Gratis telefonisch advies</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.85rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span>Geen verborgen kosten</span>
                    </li>
                  </ul>
                  <a href={url.offerte()} className="btn-meer" style={{ marginTop: 20 }}>Vraag offerte aan →</a>
                </div>

                <div style={{ background: 'var(--navy)', color: 'white', borderRadius: 'var(--radius)', padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--orange)' }}>
                    <span>●</span> Spoedhulp 24/7
                  </div>
                  <div style={{ marginTop: 12, fontSize: '1.4rem', fontWeight: 800 }}>085 500 50 50</div>
                  <p style={{ margin: '8px 0 16px', color: 'rgba(255,255,255,.7)', fontSize: '.85rem' }}>
                    Direct hulp nodig in {stad.naam}? Wij staan er.
                  </p>
                  <a href="tel:0855005050" className="btn-meer">
                    <IconPhone width="14" height="14" /> Bel direct
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>
      )}

      {/* ─── Stad-specifieke context (anti-doorway) ──────────────────
          Toont per stad+plaag UNIEKE varianten van section-tag, heading,
          intro, midden en slot-alinea. Selectie deterministisch via hash
          van (stad.slug + plaag.slug) zodat:
            - Zelfde combo = zelfde tekst (stabiel tussen builds)
            - Verschillende combo's = lexicaal anders
            - 6^5 = 7776 mogelijke combinaties = sub-80% similarity
          Voor 95/117 steden injecteren we daarnaast unieke CITY_CONTENT
          paragrafen (p1, p2). Voor de 22 steden zonder gebruiken we
          fallback-varianten die provincie + inwoneraantal mengen. */}
      {(() => {
        const cc = CITY_CONTENT[stad.slug];
        // Deterministische hash op basis van stad+plaag (verschilt per combo).
        // Voor elk tekstdeel berekenen we een aparte hash met sub-key zodat
        // varianten ONAFHANKELIJK gekozen worden (anders kunnen 2 steden
        // toevallig 3 identieke varianten krijgen — den-haag↔huizen op 98%).
        const baseKey = stad.slug + '_' + plaag.slug;
        const hashOf = (s) => {
          let h = 0;
          for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
          return h;
        };
        // Subkey per tekstdeel — hashes verschillen onafhankelijk
        const pick = (arr, part) => arr[hashOf(baseKey + ':' + part) % arr.length];

        // 6 varianten per tekst-deel (samen 7776 mogelijke combinaties)
        const tags = [
          `Lokale context ${stad.naam}`,
          `${stad.naam}-aanpak`,
          `Onze ervaring in ${stad.naam}`,
          `${plaag.nl} in ${stad.naam}`,
          `${stad.naam} & omgeving`,
          `Werkgebied ${stad.naam}`,
        ];
        const headings = [
          <>{plaag.nl} in {stad.naam}: <em>wat onze bestrijders zien</em></>,
          <>Onze ervaring met <em>{plaagLow}bestrijding in {stad.naam}</em></>,
          <>{stad.naam} en {plaagLow}: <em>lokale aanpak</em></>,
          <>Wat maakt <em>{plaagLow}bestrijding in {stad.naam}</em> bijzonder</>,
          <>{stad.naam}-team: <em>specialisten voor {plaagLow}</em></>,
          <>Hoe wij <em>{plaagLow} in {stad.naam}</em> aanpakken</>,
        ];

        // 6 fallback intro-varianten (voor steden zonder CITY_CONTENT)
        const introVariants = [
          `${stad.naam} ligt in ${stad.provincie} en telt circa ${stad.inwoners} inwoners. Onze bestrijders zijn hier dagelijks actief, vooral voor ${plaagLow}-meldingen vanuit zowel woonwijken als bedrijfspanden. De mix van oudere en nieuwere bouw in ${stad.naam} betekent dat wij verschillende methoden combineren om effectief resultaat te leveren.`,
          `Voor ${plaagLow}bestrijding in ${stad.naam} (gemeente met ${stad.inwoners} inwoners in ${stad.provincie}) hanteren wij dezelfde grondige aanpak als in alle steden in ons werkgebied: telefonische intake, vaste prijs vooraf, dichtstbijzijnde specialist binnen 24 uur op locatie. ${stad.naam} kent zowel woon- als bedrijfsverkeer, wat verschillende ${plaagLow}-situaties oplevert die wij regelmatig tegenkomen.`,
          `Wij krijgen regelmatig oproepen voor ${plaagLow} in ${stad.naam}, een gemeente in ${stad.provincie} met ${stad.inwoners} inwoners. De vragen variëren van particuliere woningen tot horeca, kantoren en VvE-complexen. Voor elke situatie hebben wij de juiste specialist, materialen en aanpak om het probleem definitief op te lossen.`,
          `In ${stad.naam} (${stad.inwoners} inwoners, ${stad.provincie}) is ${plaagLow} een terugkerend onderwerp, vooral in bepaalde seizoenen. Onze gecertificeerde bestrijders kennen het werkgebied goed en weten welke wijken en pandtypes extra aandacht vragen. Daarom kunnen wij snel en gericht handelen, geen verloren tijd.`,
          `Bij ${plaagLow}bestrijding in ${stad.naam} merken wij dat lokale kennis het verschil maakt. ${stad.naam} ligt in ${stad.provincie} en heeft ongeveer ${stad.inwoners} inwoners. De combinatie van pandtypes, infrastructuur en omgeving bepaalt vaak welke specifieke vorm van ${plaagLow} we tegenkomen, en welke aanpak het beste werkt.`,
          `${stad.naam} en omliggende gemeenten in ${stad.provincie} bedienen wij dagelijks. Voor ${plaagLow} hebben wij in deze regio jarenlange ervaring opgebouwd. De ${stad.inwoners} inwoners die er wonen, plus de bedrijfspanden, zorgen voor een continue stroom aan meldingen, waardoor onze bestrijders deze stad én de typische problemen er goed kennen.`,
        ];

        // 6 fallback midden-varianten (over werkgebied en bereikbaarheid)
        const middleVariants = [
          `Onze gecertificeerde specialisten zijn meestal binnen 24 uur in ${stad.naam} ter plaatse, vaak veel sneller bij spoedmeldingen. Wij rijden vanuit verschillende uitvalsbasis in ${stad.provincie} zodat reactietijden kort blijven, ongeacht waar in de gemeente je woont of werkt.`,
          `Of je nu in het centrum van ${stad.naam} woont, in een buitenwijk of in een omliggend dorp, onze bestrijders kennen het gebied en zijn snel op locatie. Voor spoed (gezondheidsrisico, kinderen, NVWA-controle) maken wij vrijwel altijd uitzonderingen en rijden wij direct uit.`,
          `Reactietijd in ${stad.naam} is gemiddeld binnen 24 uur, bij spoedgevallen vaak veel sneller. Wij werken met partners in ${stad.provincie} zodat de dichtstbijzijnde bestrijder altijd beschikbaar is, ongeacht het moment van de dag of week.`,
          `In ${stad.naam} en omgeving zijn wij dagelijks actief. De gemiddelde reactietijd ligt onder 24 uur, en voor échte spoed (allergie, kinderen, horeca-inspectie) komen wij vrijwel altijd diezelfde dag uit. Bel 085 500 50 50 voor directe intake.`,
          `Voor inwoners en bedrijven in ${stad.naam} hebben wij een vaste planning-cyclus: telefonische intake binnen minuten, afspraak binnen 1-3 werkdagen, of spoeduitruk binnen uren bij urgente situaties. De service is identiek voor particulier, zakelijk, VvE en horeca.`,
          `Onze ${plaagLow}-bestrijders rijden vanuit verschillende plaatsen in ${stad.provincie}, daardoor zijn de reactietijden in ${stad.naam} gemiddeld onder de 24 uur. Voor planningen werken wij flexibel: ochtend, middag of avond, een tijdslot dat jou uitkomt.`,
        ];

        // 6 slot-varianten (CTA + workflow uitleg)
        const slotVariants = [
          `Specifiek voor ${plaagLow}bestrijding in ${stad.naam} start onze aanpak met een telefonische intake. Wij geven direct een vaste prijsindicatie en plannen, bij spoed vaak diezelfde dag, een bezoek van een gecertificeerde ${plaagLow}-specialist. Na behandeling volgt nazorg en bij sommige plaagsoorten een controlebezoek na 2-4 weken.`,
          `Een opdracht voor ${plaagLow} in ${stad.naam} begint altijd met een korte intake: wat speelt er, waar, hoe urgent? Wij geven een vaste prijs aan de telefoon, plannen het bezoek, voeren de behandeling uit en geven nazorginstructies. Bij grotere plagen of hardnekkige soorten plannen wij automatisch een nacontrole.`,
          `Voor ${plaagLow}-meldingen in ${stad.naam} hanteren wij een 4-stappen-proces: intake (telefonisch, gratis), inschatting (vaste prijs vooraf), behandeling (door gecertificeerde specialist) en nazorg (instructies + indien nodig controlebezoek). Vanaf eerste belletje tot eindresultaat houden wij telefonisch contact.`,
          `Bel 085 500 50 50 voor ${plaagLow}bestrijding in ${stad.naam}, wij nemen meestal binnen 11 seconden op met een vakman aan de lijn. De bestrijder die jou helpt is dezelfde die intake doet, dus geen miscommunicatie tussen verschillende mensen. Vaste prijs aan de telefoon, behandeling vaak nog diezelfde week.`,
          `Wij maken ${plaagLow}bestrijding in ${stad.naam} zo eenvoudig mogelijk: bel, leg uit wat speelt, ontvang vaste prijsindicatie, kies een tijdslot, bestrijder komt langs, probleem opgelost. Bij plagen die meerdere bezoeken vragen (papiervisjes, hardnekkige rattenpopulaties) bespreken wij vooraf alle stappen + kosten, zodat je weet wat te verwachten.`,
          `Heb je ${plaagLow} in ${stad.naam} en wil je het snel oplossen? Bel 085 500 50 50 of vraag online een offerte aan via /offerte. Wij reageren binnen 24 uur op werkdagen, voor spoed direct. Vaste prijs, geen verborgen kosten, gecertificeerde specialist op locatie.`,
        ];

        return (
          <section className="section section-bg">
            <div className="container" style={{ maxWidth: 860 }}>
              <div className="sh">
                <div className="section-tag">{pick(tags, 'tag')}</div>
                <h2>{pick(headings, 'h2')}</h2>
              </div>
              <div style={{ display: 'grid', gap: 16, color: 'var(--text)', lineHeight: 1.75, fontSize: '1rem' }}>
                <p>{cc?.p1 || pick(introVariants, 'intro')}</p>
                <p>{cc?.p2 || pick(middleVariants, 'middle')}</p>
                <p>{pick(slotVariants, 'slot')}</p>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ─── FAQ ───────────────────────────────────────────── */}
      <section className="section faq-section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">FAQ</div>
            <h2>Veelgestelde vragen over <em>{plaagLow}bestrijding in {stad.naam}</em></h2>
          </div>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <div key={i} className={"faq-item" + (openFaq === i ? " open" : "")}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i}>
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-answer"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ANDERE DIENSTEN, slideshow van alle plagen ─── */}
      <AndereDienstenCarousel
        stadSlug={stad.slug}
        excludeSlug={plaag.slug}
        titleStad={stad.naam}
      />

      {/* ─── FINAL CTA ─────────────────────────────────────── */}
      <section className="cta-block">
        <div className="container">
          <div className="cta-block-inner">
            <h2>Klaar om uw {plaagLow}probleem in {stad.naam} op te lossen?</h2>
            <p>
              Bel ons direct of vraag een gratis offerte aan. Wij zijn bereikbaar van ma t/m zo, 07:00-22:00.
              Spoed? Ook 's nachts bereikbaar voor inwoners van {stad.naam} en omgeving.
            </p>
            <div className="cta-block-buttons">
              <a href="tel:0855005050" className="cta-btn cta-btn-primary">
                <IconPhone width="18" height="18" /> 085 500 50 50
              </a>
              <a href={url.offerte()} className="cta-btn cta-btn-secondary">
                Offerte aanvragen
              </a>
            </div>
            <div className="cta-block-trust">
              ✓ Gratis advies &nbsp;·&nbsp; ✓ Geen verborgen kosten &nbsp;·&nbsp; ✓ Gecertificeerde partners
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
